import { Component } from '@theme/component';

const ACTIVE_CLASS = 'page-navi-yokomo__link--active';

/**
 * @typedef {Object} PageNaviRefs
 * @property {HTMLAnchorElement[]} links
 */

/**
 * In-page navigation rendered by `sections/page-navi-yokomo.liquid`.
 *
 * The section sticks below the header, so the link for the section currently
 * being read is marked as current while scrolling. The navigation's own height
 * is published as `--page-navi-height` so anchor targets can clear it
 * (`scroll-margin-top`).
 *
 * @extends {Component<PageNaviRefs>}
 */
export class PageNaviYokomoComponent extends Component {
  requiredRefs = ['links'];

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {{ link: HTMLAnchorElement, target: HTMLElement }[]} */
  #entries = [];
  #current = -1;
  #frame = 0;
  #focusFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#entries = this.refs.links.flatMap((link) => {
      const target = document.getElementById(link.dataset.target ?? '');
      return target ? [{ link, target }] : [];
    });
    if (!this.#entries.length) return;

    this.#abort = new AbortController();
    window.addEventListener('scroll', () => this.#scheduleUpdate(), {
      passive: true,
      signal: this.#abort.signal,
    });
    document.addEventListener('focusin', (event) => this.#keepFocusVisible(event), {
      signal: this.#abort.signal,
    });

    const bar = this.closest('.page-navi-yokomo') ?? this;
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const height = Math.round(entry?.borderBoxSize[0]?.blockSize ?? 0);
      document.body.style.setProperty('--page-navi-height', `${height}px`);
      this.#scheduleUpdate();
    });
    this.#resizeObserver.observe(bar);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#focusFrame);
    document.body.style.removeProperty('--page-navi-height');
  }

  #scheduleUpdate() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  /**
   * Marks the last section scrolled up to its landing position (where a link click
   * puts it, i.e. its `scroll-margin-top`). At the bottom of the page the last section
   * cannot reach that position, so it is marked once the page can scroll no further.
   */
  #update() {
    let current = 0;
    for (const [index, { target }] of this.#entries.entries()) {
      const landing = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      if (target.getBoundingClientRect().top <= landing + 1) current = index;
    }
    const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;
    if (atBottom) current = this.#entries.length - 1;
    if (current === this.#current) return;

    this.#current = current;
    const currentLink = this.#entries[current].link;
    // Clear every link, including ones whose target is missing, so only one stays current.
    for (const link of this.refs.links) {
      const isCurrent = link === currentLink;
      link.classList.toggle(ACTIVE_CLASS, isCurrent);
      if (isCurrent) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
    this.#revealLink(currentLink);
  }

  /**
   * Moving focus backwards scrolls the focused element to the top of the viewport,
   * where it ends up hidden behind the sticky header and this navigation
   * (WCAG 2.4.11 Focus Not Obscured). Scroll it back into the visible area.
   * @param {FocusEvent} event
   */
  #keepFocusVisible(event) {
    const element = event.target;
    if (!(element instanceof HTMLElement)) return;
    // Page scrolling cannot move content inside dialogs or drawers, so leave those alone.
    if (!element.closest('main') || element.closest('dialog, [role="dialog"], [aria-modal="true"]')) return;

    // Only content after the navigation can slide underneath it (the header and hero cannot).
    const bar = this.closest('.page-navi-yokomo') ?? this;
    if (!(bar.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
    if (bar.contains(element)) return;

    const barBottom = bar.getBoundingClientRect().bottom;
    const { top } = element.getBoundingClientRect();
    if (top >= barBottom) return;

    cancelAnimationFrame(this.#focusFrame);
    this.#focusFrame = requestAnimationFrame(() => {
      const hidden = barBottom - element.getBoundingClientRect().top;
      if (hidden > 0) window.scrollBy({ top: -hidden - 16, behavior: 'auto' });
    });
  }

  /**
   * Scrolls the navigation (not the page) horizontally so the current link is visible.
   * @param {HTMLAnchorElement} link
   */
  #revealLink(link) {
    const nav = link.closest('nav');
    if (!nav || nav.scrollWidth <= nav.clientWidth) return;

    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.left >= navRect.left && linkRect.right <= navRect.right) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    nav.scrollTo({
      left: nav.scrollLeft + linkRect.left - navRect.left,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('page-navi-yokomo-component')) {
  customElements.define('page-navi-yokomo-component', PageNaviYokomoComponent);
}
